import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { fmt } from '../chart-utils';

const COLOR_A = '#7c9cff';
const COLOR_B = '#58e1c1';
const FILL_A = 'rgba(124,156,255,0.10)';
const FILL_B = 'rgba(88,225,193,0.08)';
const COLOR_GRID = 'rgba(255,255,255,0.08)';
const COLOR_LABEL = 'rgba(255,255,255,0.68)';

@Component({
  selector: 'app-line-chart',
  templateUrl: './line-chart.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LineChart {
  readonly seriesA = input<number[]>([]);
  readonly seriesB = input<number[]>([]);

  private readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>('canvas');
  private readonly destroyRef = inject(DestroyRef);
  private resizeObserver: ResizeObserver | null = null;

  constructor() {
    effect(() => {
      const canvasEl = this.canvasRef();
      const a = this.seriesA();
      const b = this.seriesB();
      if (!canvasEl || !a.length) return;
      const canvas = canvasEl.nativeElement;
      if (canvas.getBoundingClientRect().width === 0) return;
      this.draw(canvas, a, b);
    });

    afterNextRender(() => {
      const canvasEl = this.canvasRef();
      if (!canvasEl) return;
      const canvas = canvasEl.nativeElement;
      this.draw(canvas, this.seriesA(), this.seriesB());
      this.resizeObserver = new ResizeObserver(() =>
        this.draw(canvas, this.seriesA(), this.seriesB()),
      );
      this.resizeObserver.observe(canvas.parentElement ?? canvas);
    });

    this.destroyRef.onDestroy(() => this.resizeObserver?.disconnect());
  }

  private draw(canvas: HTMLCanvasElement, seriesA: number[], seriesB: number[]): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const pad = { top: 22, right: 20, bottom: 42, left: 60 };
    const chartW = w - pad.left - pad.right;
    const chartH = h - pad.top - pad.bottom;
    const maxValue = Math.max(...seriesA, ...seriesB) * 1.1 || 1;
    const steps = 5;

    ctx.clearRect(0, 0, w, h);
    ctx.font = '12px Inter, sans-serif';

    for (let i = 0; i <= steps; i++) {
      const y = pad.top + (chartH / steps) * i;
      ctx.beginPath();
      ctx.strokeStyle = COLOR_GRID;
      ctx.moveTo(pad.left, y);
      ctx.lineTo(w - pad.right, y);
      ctx.stroke();
      ctx.fillStyle = COLOR_LABEL;
      ctx.fillText(fmt(Math.round(maxValue - (maxValue / steps) * i)), 4, y + 4);
    }

    const labelsEvery = Math.ceil(seriesA.length / 8);
    for (let i = 0; i < seriesA.length; i++) {
      if (i % labelsEvery === 0 || i === seriesA.length - 1) {
        const x = pad.left + (i / (seriesA.length - 1)) * chartW;
        ctx.fillStyle = COLOR_LABEL;
        ctx.fillText(String(i + 1), x - 4, h - 16);
      }
    }

    this.plotLine(ctx, seriesA, COLOR_A, FILL_A, pad, chartW, chartH, maxValue);
    this.plotLine(ctx, seriesB, COLOR_B, FILL_B, pad, chartW, chartH, maxValue);
  }

  private plotLine(
    ctx: CanvasRenderingContext2D,
    series: number[],
    color: string,
    fill: string,
    pad: { top: number; right: number; bottom: number; left: number },
    chartW: number,
    chartH: number,
    maxValue: number,
  ): void {
    const n = series.length;
    ctx.beginPath();
    series.forEach((v, i) => {
      const x = pad.left + (i / (n - 1)) * chartW;
      const y = pad.top + chartH - (v / maxValue) * chartH;
      i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
    });
    ctx.strokeStyle = color;
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.lineTo(pad.left + chartW, pad.top + chartH);
    ctx.lineTo(pad.left, pad.top + chartH);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();

    series.forEach((v, i) => {
      const x = pad.left + (i / (n - 1)) * chartW;
      const y = pad.top + chartH - (v / maxValue) * chartH;
      ctx.beginPath();
      ctx.arc(x, y, 3.2, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    });
  }
}
